/**
 * カラーパレット
 * アプリケーション全体で使用する色の定義
 *
 * このファイルには以下のカテゴリが含まれます：
 * - プライマリカラー: メインブランドカラー
 * - セカンダリカラー: サブブランドカラー
 * - アクセントカラー: 強調表示用の色
 * - ニュートラルカラー: グレースケール
 * - 背景色: 背景に使用する色
 * - テキストカラー: テキストに使用する色
 * - ボーダーカラー: 境界線に使用する色
 * - セマンティックカラー: エラー、警告、情報、成功などの意味を持つ色
 * - フォーカスモード専用カラー: フォーカスモード時のダークテーマ用の色
 */

/**
 * カラーパレット - 現代的で洗練された色彩設計
 */
export const COLORS = {
  // プライマリカラー - より洗練されたブルー系
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3',
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  } as const,

  // セカンダリカラー - エレガントなピンク系
  secondary: {
    50: '#fce4ec',
    100: '#f8bbd9',
    200: '#f48fb1',
    300: '#f06292',
    400: '#ec407a',
    500: '#e91e63',
    600: '#d81b60',
    700: '#c2185b',
    800: '#ad1457',
    900: '#880e4f',
    main: '#e91e63',
    light: '#f06292',
    dark: '#c2185b',
    contrastText: '#ffffff',
  } as const,

  // アクセントカラー
  accent: {
    purple: '#9c27b0',
    indigo: '#3f51b5',
    teal: '#009688',
    orange: '#ff9800',
    amber: '#ffc107',
  } as const,

  // ニュートラルカラー - より豊富なグレーパレット
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  } as const,

  // 背景色
  // 通常表示モードをより洗練された薄いブルー基調に
  // Google Wallet のような穏やかなトーンを意識
  background: {
    // ページ全体の下地となる色（ごく淡いブルー）
    default: '#F3F7FF', // さらに2段階薄く（ごく淡いブルー）
    // コンテンツ面（カード/セクション）は白でクリーンに
    paper: '#ffffff',
    // 浮いた面（ダイアログ等）も白を基本に
    elevated: '#ffffff',
    overlay: 'rgba(0, 0, 0, 0.5)',
  } as const,

  // テキストカラー
  text: {
    primary: '#212121',
    secondary: '#616161',
    disabled: '#9e9e9e',
    hint: '#bdbdbd',
  } as const,

  // ボーダーカラー（薄いブルーに寄せて柔らかい印象へ）
  border: {
    light: '#E3ECF7',
    main: '#CFDBED',
    dark: '#B8C6DA',
  } as const,

  // セマンティックカラー
  semantic: {
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      bg: '#ffebee',
      contrastText: '#ffffff',
    } as const,
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      bg: '#fff3e0',
      contrastText: '#ffffff',
    } as const,
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      bg: '#e3f2fd',
      contrastText: '#ffffff',
    } as const,
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      bg: '#e8f5e8',
      contrastText: '#ffffff',
    } as const,
  } as const,

  // ダークモード用カラー
  // ダークモード用カラー（フォーカスモードと同じ配色）
  darkMode: {
    background: {
      default: '#050505', // より深いモダンな黒
      paper: '#121212', // 落ち着いたダークグレー
    },
    text: {
      primary: '#f5f5f5', // 完全に真っ白ではない、目に優しい白
      secondary: '#a0a0a0',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: '#272727', // 控えめなボーダー
    action: {
      active: '#fff',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  } as const,

  // フォーカスモード専用カラー
  focusMode: {
    // 背景色 - モダンで深みのある黒
    background: {
      primary: '#050505',
      secondary: '#121212',
      tertiary: '#1e1e1e',
      overlay: 'rgba(0, 0, 0, 0.8)',
    } as const,

    // テキスト色 - コントラストを保ちつつ目に優しく
    text: {
      primary: '#f5f5f5',
      secondary: '#a0a0a0',
      tertiary: '#666666',
      disabled: '#404040',
      accent: '#64b5f6',
    } as const,

    // ボーダー色 - 非常に控えめ
    border: {
      primary: '#272727',
      secondary: '#333333',
      accent: '#64b5f6',
      subtle: 'rgba(255, 255, 255, 0.05)',
    } as const,

    // 入力フィールド
    input: {
      background: '#121212',
      border: '#272727',
      borderFocused: '#64b5f6',
      placeholder: '#666666',
      text: '#f5f5f5',
    } as const,

    // ボタン
    button: {
      primary: {
        background: '#64b5f6',
        text: '#000000',
        border: '#64b5f6',
        hover: '#90caf9',
      } as const,
      secondary: {
        background: 'transparent',
        text: '#f5f5f5',
        border: '#272727',
        hover: '#1e1e1e',
      } as const,
      ghost: {
        background: 'transparent',
        text: '#a0a0a0',
        border: 'transparent',
        hover: '#1e1e1e',
      } as const,
    } as const,

    // カード・パネル
    card: {
      background: '#121212',
      border: '#272727',
      shadow: '0 4px 20px rgba(0, 0, 0, 0.8)',
    } as const,

    // メニュー・ドロップダウン
    menu: {
      background: '#121212',
      border: '#272727',
      hover: '#1e1e1e',
      shadow: '0 8px 32px rgba(0, 0, 0, 0.9)',
    } as const,

    // スクロールバー
    scrollbar: {
      track: '#050505',
      thumb: '#333333',
      thumbHover: '#444444',
    } as const,

    // アコーディオン
    accordion: {
      summary: {
        background: 'rgba(28, 28, 28, 0.95)',
        backgroundHover: 'rgba(32, 32, 32, 0.98)',
        backgroundExpanded: 'rgba(36, 36, 36, 0.98)',
        border: 'rgba(255, 255, 255, 0.06)',
      },
      root: {
        background: 'rgba(16, 16, 16, 0.96)',
        border: 'rgba(255, 255, 255, 0.04)',
      },
      details: {
        background: 'rgba(12, 12, 12, 0.96)',
        border: 'rgba(255, 255, 255, 0.06)',
      },
    } as const,
  } as const,

  // ライトモード用スクロールバー
  scrollbar: {
    track: 'transparent',
    thumb: 'rgba(0, 0, 0, 0.2)',
  } as const,

  // ダークモード用スクロールバー
  darkScrollbar: {
    track: '#2b2b2b',
    thumb: '#6b6b6b',
    thumbHover: '#959595',
  } as const,

  // 共通のオーバーレイ・透過色
  overlay: {
    dark: 'rgba(0, 0, 0, 0.7)',
    light: 'rgba(255, 255, 255, 0.95)',
    backdrop: 'rgba(0, 0, 0, 0.5)',
  } as const,

  // フォーカスモード用ナビゲーション
  focusModeNav: {
    background: 'rgba(0, 0, 0, 0.7)',
    text: 'rgba(255, 255, 255, 0.95)',
    border: 'rgba(255, 255, 255, 0.3)',
    indicator: {
      background: 'rgba(26, 26, 26, 0.9)',
      text: 'rgba(255, 255, 255, 0.9)',
    },
    button: {
      surface: 'rgba(255, 255, 255, 0.08)',
      border: 'rgba(255, 255, 255, 0.45)',
      hoverSurface: 'rgba(255, 255, 255, 0.18)',
      hoverBorder: 'rgba(255, 255, 255, 0.65)',
      activeSurface: 'rgba(255, 255, 255, 0.12)',
    },
  } as const,

  // 従来との互換性
  textSecondary: '#616161',
  error: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
  success: '#4caf50',
} as const;
